<script>
import moment from "moment";
import axios from '@/utils/axios';
import { clientAdmin } from "@/utils/auth";
import { useLeadMetaStore } from '@/stores/useLeadMetaStore';

export default {
  inject: ['setLoading'],
  data() {
    return {
      errors: {},
      title: "Reports",
      text: "HRM",
      text1: "New Joiners Report",
      clientAdmin: clientAdmin(),
      filter: {
        month: Number(moment().format('M')),
        year: Number(moment().format('YYYY')),
        branch_id: '',
        department_id: '',
      },
      months: [
        { id: 1, name: 'January' }, { id: 2, name: 'February' }, { id: 3, name: 'March' },
        { id: 4, name: 'April' }, { id: 5, name: 'May' }, { id: 6, name: 'June' },
        { id: 7, name: 'July' }, { id: 8, name: 'August' }, { id: 9, name: 'September' },
        { id: 10, name: 'October' }, { id: 11, name: 'November' }, { id: 12, name: 'December' },
      ],
      years: [],
      summary: {},
      data: [],
      departments: [],
    };
  },

  computed: {
    branches() {
      return useLeadMetaStore().branches || [];
    },
  },

  mounted() {
    const currentYear = moment().year();
    for (let y = currentYear; y >= currentYear - 5; y--) {
      this.years.push({ id: y, name: String(y) });
    }
    this.getDropdowns();
    this.filterReport();
  },

  methods: {

    async exportCSV() {
      this.setLoading(true);
      try {
        const payload = { export: 'csv', ...this.filter };
        const query_string = new URLSearchParams(payload).toString();
        const response = await axios.get(`/hrms/reports/new-joiners?${query_string}`, {
          responseType: 'blob',
        });
        const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `new-joiners-report-${this.filter.month}-${this.filter.year}.csv`;
        link.click();
        URL.revokeObjectURL(url);
      } catch (error) {
        this.$swalToast('Failed to export CSV', 'error');
      } finally {
        this.setLoading(false);
      }
    },

    async filterReport() {
      this.errors = {};
      this.setLoading(true);
      try {
        const query_string = new URLSearchParams(this.filter).toString();
        const response = await axios.get(`/hrms/reports/new-joiners?${query_string}`);
        this.summary = response.data.data.summary || {};
        this.data = response.data.data.rows || [];
      } catch (error) {
        if (error.type == 'validation') {
          this.errors = error.errors;
        } else {
          this.$swalToast(error.message, 'error');
        }
      } finally {
        this.setLoading(false);
      }
    },

    async getDropdowns() {
      await useLeadMetaStore().loadAll();
      try {
        const deptRes = await axios.get('/hrms/departments/dropdown');
        this.departments = deptRes.data.data || [];
      } catch (error) {
        console.log('Dropdown load error:', error.message);
      }
    },

    hasActiveFilter() {
      const currentMonth = String(moment().month() + 1);
      const currentYear = String(moment().year());
      return this.filter.branch_id || this.filter.department_id
        || String(this.filter.month) !== currentMonth || String(this.filter.year) !== currentYear;
    },

    clearFilter() {
      this.filter.branch_id = '';
      this.filter.department_id = '';
      this.filter.month = moment().format('M');
      this.filter.year = moment().format('YYYY');
      this.filterReport();
    },

    formatDate(val) {
      if (!val) return '-';
      return moment(val).format('DD MMM YYYY');
    },

    employmentTypeLabel(val) {
      const map = {
        full_time: 'Full Time',
        part_time: 'Part Time',
        contract: 'Contract',
        intern: 'Intern',
      };
      return map[val] || (val || '-');
    },
  }
};
</script>

<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <div class="page-wrapper">
    <div class="content">

      <!-- Breadcrumb -->
      <div class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
        <breadcrumb :title="title" :text="text" :text1="text1" />
        <div class="d-flex my-xl-auto right-content align-items-center flex-wrap">
          <button class="btn btn-outline-primary d-flex align-items-center" @click="exportCSV" :disabled="!data || !data.length">
            <i class="ti ti-file-type-csv me-2"></i>Export CSV
          </button>
        </div>
      </div>

      <!-- Summary Cards -->
      <div class="row g-3 mb-3">
        <div class="col-md-3 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">Total Joiners</span>
              <span class="fw-bold text-dark" style="font-size:20px;">{{ summary.total_joiners }}</span>
            </div>
          </div>
        </div>
        <div class="col-md-3 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">Full Time</span>
              <span class="fw-bold text-dark" style="font-size:20px;">{{ summary.full_time }}</span>
            </div>
          </div>
        </div>
        <div class="col-md-3 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">Part Time</span>
              <span class="fw-bold text-dark" style="font-size:20px;">{{ summary.part_time }}</span>
            </div>
          </div>
        </div>
        <div class="col-md-3 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">Contract</span>
              <span class="fw-bold text-dark" style="font-size:20px;">{{ summary.contract }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- By Department Breakdown -->
      <div class="d-flex flex-wrap gap-2 mb-3" v-if="summary.by_department && summary.by_department.length">
        <span class="badge border text-dark" style="font-size:12px;" v-for="(d, i) in summary.by_department" :key="i">
          {{ d.department }}: <strong>{{ d.count }}</strong>
        </span>
      </div>

      <!-- Filters -->
      <div class="card border-0">
        <div class="card-body d-flex align-items-center justify-content-center flex-wrap pb-1">
          <div class="d-flex align-items-center flex-wrap mb-1 gap-2">
            <div class="me-3" v-if="hasActiveFilter()">
              <a href="javascript:void(0);" class="text-danger" @click="clearFilter()"><i class="ti ti-filter-x"></i></a>
            </div>
            <div class="me-3">
              <vue-select v-model="filter.month" :options="months" label="name" :reduce="m => m.id"
                placeholder="Month" @update:modelValue="filterReport()" style="min-width: 140px;"></vue-select>
            </div>
            <div class="me-3">
              <vue-select v-model="filter.year" :options="years" label="name" :reduce="y => y.id"
                placeholder="Year" @update:modelValue="filterReport()" style="min-width: 120px;"></vue-select>
            </div>
            <div class="me-3">
              <vue-select v-model="filter.branch_id" :options="branches" label="name" :reduce="b => b.id"
                placeholder="Branch" :clearable="true" @update:modelValue="filterReport()" style="min-width: 180px;"></vue-select>
            </div>
            <div class="me-3">
              <vue-select v-model="filter.department_id" :options="departments" label="name" :reduce="d => d.id"
                placeholder="Department" :clearable="true" @update:modelValue="filterReport()" style="min-width: 180px;"></vue-select>
            </div>
          </div>
        </div>
      </div>

      <!-- Table -->
      <div class="row">
        <div class="col-lg-12">
          <div class="card-body card-table-wrapper">
            <div class="custom-datatable-filter table-responsive">
              <table class="card-table" v-if="data && data.length">
                <thead>
                    <tr>
                    <th>#</th>
                    <th>Employee</th>
                    <th>Department</th>
                    <th>Position</th>
                    <th>Employment Type</th>
                    <th>Joining Date</th>
                    <th>Manager</th>
                    </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, index) in data" :key="row.id || index">
                        <td>{{ index + 1 }}.</td>
                        <td>
                            <strong class="text-dark">{{ row.full_name || '-' }}</strong>
                            <small class="text-muted d-block" style="font-size:11px;">{{ row.code || '' }}</small>
                        </td>
                        <td>{{ row.department?.name || 'Unassigned' }}</td>
                        <td>{{ row.position?.name || '-' }}</td>
                        <td>{{ employmentTypeLabel(row.employment_type) }}</td>
                        <td>{{ formatDate(row.joining_date) }}</td>
                        <td>
                          <span v-if="row.is_manager" class="badge bg-info-transparent">Manager</span>
                          <span v-else class="text-muted">-</span>
                        </td>
                        </tr>
                </tbody>
              </table>
              <div v-else class="p-3 text-center text-muted">No records found.</div>
            </div>
          </div>
        </div>
      </div>

    </div>
    <app-footer></app-footer>
  </div>
</template>